/**
 * Part selector — renders one CatalogPart with field dropdowns and an Add button.
 *
 * Supports two modes:
 * - **Icon-marking** (default): All options visible with tick/X marks.
 * - **Cascading** (part.cascadeFilter): Top-down sequential selection; only
 *   compatible values shown; changing a field clears all fields below it.
 *
 * For bifurcated_down/contra/ipsi types, this is rendered twice (left + right)
 * by the parent component.
 */

import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FieldDropdown } from './field-dropdown';

import { Button } from '@/components/ui/button';
import type {
    FieldCompatibilityMap,
    FieldSelections,
    IntroductionSide,
    MeasurementLookup,
    ICatalogPart,
    Side,
} from '@/workflows/vascular/common/catalog';
import {
    computeAllFieldsCompatibility,
    computeAllCascadingOptions,
    getDistinctValues,
    resolveReference,
} from '@/workflows/vascular/common/catalog';

interface PartSelectorProps {
    part: ICatalogPart;
    side: Side;
    introductionSide: IntroductionSide;
    measurementLookup: MeasurementLookup;
    onAdd: (partId: string, side: Side, fieldSelections: FieldSelections) => void;
    disabled?: boolean;
}

export function PartSelector({
    part,
    side,
    introductionSide,
    measurementLookup,
    onAdd,
    disabled,
}: PartSelectorProps) {
    const isCascade = part.cascadeFilter === true;

    return isCascade ? (
        <CascadePartSelector
            part={part}
            side={side}
            introductionSide={introductionSide}
            measurementLookup={measurementLookup}
            onAdd={onAdd}
            disabled={disabled}
        />
    ) : (
        <IconMarkingPartSelector
            part={part}
            side={side}
            introductionSide={introductionSide}
            measurementLookup={measurementLookup}
            onAdd={onAdd}
            disabled={disabled}
        />
    );
}

// ============================================================================
// Icon-Marking Mode (Mode B) — original behavior
// ============================================================================

function IconMarkingPartSelector({
    part,
    side,
    introductionSide,
    measurementLookup,
    onAdd,
    disabled,
}: PartSelectorProps) {
    const [selections, setSelections] = useState<FieldSelections>({});

    const compatibility = useMemo(() => computeAllFieldsCompatibility(part, selections), [part, selections]);

    const ref = useMemo(() => resolveReference(part, selections), [part, selections]);

    // Auto-select fields that have only one possible value
    useEffect(() => {
        let updated = false;
        const next = { ...selections };
        for (const field of part.fields) {
            if (next[field.column] != null) continue;
            const distinct = getDistinctValues(part.items, field.column, next);
            if (distinct.size === 1) {
                next[field.column] = [...distinct][0];
                updated = true;
            }
        }
        if (updated) setSelections(next);
    }, [part, selections]);

    const handleFieldChange = useCallback((column: string, value: string | number | null) => {
        setSelections((prev) => ({ ...prev, [column]: value }));
    }, []);

    const handleAdd = useCallback(() => {
        onAdd(part.id, side, selections);
    }, [part.id, side, selections, onAdd]);

    return (
        <PartSelectorLayout part={part} side={side} ref={ref} disabled={disabled} onAdd={handleAdd}>
            {part.fields.map((field) => (
                <FieldDropdown
                    key={field.column}
                    field={field}
                    currentValue={selections[field.column] ?? null}
                    compatibilityMap={compatibility.get(field.column) ?? new Map()}
                    measurementLookup={measurementLookup}
                    side={side}
                    introductionSide={introductionSide}
                    onChange={(value) => handleFieldChange(field.column, value)}
                />
            ))}
        </PartSelectorLayout>
    );
}

// ============================================================================
// Cascading Mode (Mode A) — Cook catalogs
// ============================================================================

function CascadePartSelector({
    part,
    side,
    introductionSide,
    measurementLookup,
    onAdd,
    disabled,
}: PartSelectorProps) {
    const [selections, setSelections] = useState<FieldSelections>({});

    // Track previous effective selections to detect auto-fill changes
    const prevEffectiveRef = useRef<FieldSelections>({});

    const { effectiveSelections, optionsPerField } = useMemo(
        () => computeAllCascadingOptions(part, selections),
        [part, selections]
    );

    // Sync auto-filled selections back to state
    useEffect(() => {
        const prev = prevEffectiveRef.current;
        let needsUpdate = false;
        for (const field of part.fields) {
            const col = field.column;
            if (effectiveSelections[col] !== prev[col]) {
                needsUpdate = true;
                break;
            }
        }
        prevEffectiveRef.current = effectiveSelections;
        if (needsUpdate) {
            setSelections(effectiveSelections);
        }
    }, [effectiveSelections, part.fields]);

    const ref = useMemo(() => resolveReference(part, effectiveSelections), [part, effectiveSelections]);

    const handleFieldChange = useCallback(
        (column: string, value: string | number | null) => {
            setSelections((prev) => {
                const next: FieldSelections = {};
                // Keep selections for fields above the changed field
                for (const field of part.fields) {
                    if (field.column === column) {
                        next[column] = value;
                        break;
                    }
                    next[field.column] = prev[field.column] ?? null;
                }
                // Fields below are cleared (cascade reset)
                return next;
            });
        },
        [part.fields]
    );

    const handleAdd = useCallback(() => {
        onAdd(part.id, side, effectiveSelections);
    }, [part.id, side, effectiveSelections, onAdd]);

    // Build compatibility maps from cascade options for FieldDropdown consumption
    const compatibilityMaps = useMemo(() => {
        const maps = new Map<string, FieldCompatibilityMap>();
        for (const [col, options] of optionsPerField) {
            const map: FieldCompatibilityMap = new Map();
            for (const v of options) {
                map.set(v, 'compatible');
            }
            maps.set(col, map);
        }
        return maps;
    }, [optionsPerField]);

    return (
        <PartSelectorLayout part={part} side={side} ref={ref} disabled={disabled} onAdd={handleAdd}>
            {part.fields.map((field, fieldIndex) => {
                // Disabled if any field above has no selection
                const fieldDisabled =
                    fieldIndex > 0 &&
                    part.fields.slice(0, fieldIndex).some((f) => effectiveSelections[f.column] == null);

                return (
                    <FieldDropdown
                        key={field.column}
                        field={field}
                        currentValue={effectiveSelections[field.column] ?? null}
                        compatibilityMap={compatibilityMaps.get(field.column) ?? new Map()}
                        measurementLookup={measurementLookup}
                        side={side}
                        introductionSide={introductionSide}
                        onChange={(value) => handleFieldChange(field.column, value)}
                        cascadeMode
                        disabled={fieldDisabled}
                    />
                );
            })}
        </PartSelectorLayout>
    );
}

// ============================================================================
// Shared Layout
// ============================================================================

interface PartSelectorLayoutProps {
    part: ICatalogPart;
    side: Side;
    ref: string | null;
    disabled?: boolean;
    onAdd: () => void;
    children: React.ReactNode;
}

function PartSelectorLayout({ part, side, ref, disabled, onAdd, children }: PartSelectorLayoutProps) {
    const sideLabel = side !== 'none' ? ` (${side === 'left' ? 'L' : 'R'})` : '';

    return (
        <div className="border-b border-border/50 px-3 py-2">
            <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-semibold text-foreground">
                    {part.label}
                    {sideLabel && <span className="ml-1 text-muted-foreground font-normal">{sideLabel}</span>}
                </h4>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] gap-1"
                    onClick={onAdd}
                    disabled={disabled || !ref}
                    title={ref ? `Add ${ref}` : 'Complete all fields to add'}
                >
                    <Plus className="h-3 w-3" />
                    {ref ?? 'Add'}
                </Button>
            </div>
            <div className="flex flex-col gap-1">{children}</div>
        </div>
    );
}
